/**
 * Fetches the member-status lookup for a challenge — tells the embedded
 * login modal which attestation / geo fields it can hide because the
 * backend already has them on file for the current partner-platform
 * visitor.
 *
 * Only fires when a partner handshake is present in Redux
 * (`rttrStore.realmUuid` + `realmTs` + `realmSig` — Minelab Realm is
 * the only platform today; the endpoint URL is platform-neutral so
 * future partners use the same hook). Web visitors who land on the
 * hub without an embed handshake get the full login flow exactly as
 * before — the hook returns `undefined` and the modal renders every
 * field.
 *
 * Backend is `GET /challenges/:slug/member-status` (no auth required —
 * the partner handshake HMAC is the auth proof). The signature
 * headers are attached automatically by `getConfig` so there is
 * nothing to pass in here.
 *
 * Cache key includes the partner UID so a logout/handoff swap forces a
 * fresh lookup, and the slug so multiple Drops can mount the hook
 * independently. Result rarely changes within a session.
 */
import {useQuery} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {ChallengeMemberStatusResponse, ChallengesApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {IStore} from "@/redux/defaultStore";

export function useGetChallengeMemberStatus(slug: string | undefined) {
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const realmTs = useSelector((store: IStore) => store.rttrStore?.realmTs);
    const realmSig = useSelector((store: IStore) => store.rttrStore?.realmSig);

    const hasHandoff = Boolean(realmUuid && realmTs && realmSig);

    return useQuery({
        /*
         * Include `realmTs` in the key so a fresh handshake with the
         * same UUID but a new timestamp forces a fresh fetch. Without
         * it, React Query would serve the stale 60s cache entry on a
         * re-embed even when the partner-side state has since changed.
         */
        queryKey: ["challenge-member-status", slug, realmUuid ?? "no-handoff", realmTs ?? "no-ts"],
        queryFn: async (): Promise<ChallengeMemberStatusResponse> => {
            const api = new ChallengesApi(getConfig());
            /*
             * The generated client requires the three handshake headers
             * as named arguments (the swagger declares them in:header,
             * required:true). `getConfig` ALSO attaches them globally
             * via Configuration headers — that's the authoritative
             * source; the named arguments here just satisfy the
             * generated signature. Passing the Redux-store values
             * directly keeps the two paths trivially in sync without
             * a config-vs-args drift surface.
             */
            return api.getChallengesSlugMemberStatus({
                slug: slug!,
                xRealmUUID: realmUuid!,
                xRealmTS: realmTs!,
                xRealmSig: realmSig!,
            });
        },
        enabled: Boolean(slug) && hasHandoff,
        staleTime: 60_000,
    });
}
